import { Plus, X } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Named lists — reusable value sets an expression references as `$lists.<name>`
 * (e.g. the businessRejections codes). Each list is a name + values, one per
 * line or comma-separated.
 */
export function ListsEditor({
  lists,
  onChange,
}: {
  lists: Record<string, unknown[]>;
  onChange: (lists: Record<string, unknown[]>) => void;
}) {
  const entries = Object.entries(lists);

  const setName = (oldName: string, newName: string) => {
    const next: Record<string, unknown[]> = {};
    for (const [k, v] of entries) next[k === oldName ? newName : k] = v;
    onChange(next);
  };
  const setValues = (name: string, text: string) => {
    onChange({ ...lists, [name]: parseValues(text) });
  };
  const remove = (name: string) => {
    const next = { ...lists };
    delete next[name];
    onChange(next);
  };
  const add = () => {
    let n = 'list';
    let i = 1;
    while (n in lists) n = `list${++i}`;
    onChange({ ...lists, [n]: [] });
  };

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="eyebrow">
          named lists <span className="normal-case tracking-normal text-text-faint">· use as $lists.name</span>
        </span>
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 font-condensed text-[9px] uppercase tracking-wider text-accent hover:text-accent-warm"
        >
          <Plus size={10} /> add list
        </button>
      </div>

      {entries.length === 0 ? (
        <p className="text-[11.5px] text-text-faint">
          No lists. Add one to reference reusable value sets from the expression.
        </p>
      ) : (
        <div className="space-y-2">
          {entries.map(([name, values]) => (
            <div key={name} className="border border-hairline">
              <div className="flex items-center gap-2 border-b border-hairline bg-surface-sunken px-2 py-1">
                <span className="font-mono text-[10px] text-text-faint">$lists.</span>
                <input
                  value={name}
                  onChange={(e) => setName(name, e.target.value)}
                  spellCheck={false}
                  className="flex-1 bg-transparent font-mono text-[12px] text-text focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => remove(name)}
                  className="text-text-faint hover:text-crit"
                  title="Remove list"
                >
                  <X size={12} />
                </button>
              </div>
              <textarea
                value={(values as unknown[]).join('\n')}
                onChange={(e) => setValues(name, e.target.value)}
                rows={Math.min(Math.max((values as unknown[]).length, 2), 6)}
                placeholder="one value per line, or comma-separated"
                spellCheck={false}
                className={cn(
                  'w-full resize-y bg-surface px-2 py-1.5 font-mono text-[11.5px] text-text',
                  'placeholder:text-text-faint focus:outline-none',
                )}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function parseValues(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}
